import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, Form, Input, message, Space, InputNumber, Card, Popconfirm, Select, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { fetchSankalpas, createSankalpa, updateSankalpa, deleteSankalpa, updateSankalpaLine, deleteSankalpaLine, addSankalpaLines } from '../store/sankalpaSlice';
import { CreateSankalpa, SankalpaLine } from '../types';
import AddSankalpaModal from '../components/AddSankalpaModal';
import AddLineModal from '../components/AddLineModal';
import SankalpaLineBhedPanel from '../components/SankalpaLineBhedPanel';
import BidhiMetadataTagsSelect from '../components/bidhi/BidhiMetadataTagsSelect';
import { collectMetadataTagsFromSankalpas, extractMetadataTagNames, buildMetadataWithTags, metadataToFormEntries } from '../utils/bidhiEditByTag';
import { sankalpaTagAPI } from '../services/api';

const SankalpaPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { sankalpas, loading } = useSelector((state: RootState) => state.sankalpa);
  const [allTags, setAllTags] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [addSankalpaModalVisible, setAddSankalpaModalVisible] = useState<boolean>(false);
  const [editSankalpaModalVisible, setEditSankalpaModalVisible] = useState<boolean>(false);
  const [editLineModalVisible, setEditLineModalVisible] = useState<boolean>(false);
  const [addLineModalVisible, setAddLineModalVisible] = useState<boolean>(false);
  const [extractModalVisible, setExtractModalVisible] = useState<boolean>(false);
  const [selectedSankalpaId, setSelectedSankalpaId] = useState<number | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [editLineContent, setEditLineContent] = useState<string>('');
  const [selectedLineContent, setSelectedLineContent] = useState<string>('');
  const [lineContent, setLineContent] = useState<string>('');
  const [lineOrder, setLineOrder] = useState<number>(1);

  const lineTagOptions = useMemo(
    () => collectMetadataTagsFromSankalpas(sankalpas),
    [sankalpas],
  );

  const [editSankalpaForm] = Form.useForm();
  const [editLineForm] = Form.useForm();
  const [addLineForm] = Form.useForm();
  const [extractForm] = Form.useForm();

  useEffect(() => {
    dispatch(fetchSankalpas());
    loadAllTags();
  }, [dispatch]);

  const loadAllTags = async () => {
    try {
      const response = await sankalpaTagAPI.getAll();
      setAllTags(response.data);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const handleAddLines = async (values: { lines: { content: string; metadata?: Record<string, any> }[] }) => {
    try {
      const currentSankalpa = sankalpas.find(s => s.id === selectedSankalpaId);
      const maxOrder = currentSankalpa?.lines?.length ? Math.max(...currentSankalpa.lines.map(line => line.order || 0)) : 0;
      
      const linesWithOrder = values.lines.map((line, index) => ({
        content: line.content,
        order: maxOrder + index + 1,
        sankalpaId: selectedSankalpaId!,
        ...(line.metadata && Object.keys(line.metadata).length > 0 ? { metadata: line.metadata } : {}),
      }));
      
      // Add lines to sankalpa
      await dispatch(addSankalpaLines({ sankalpaId: selectedSankalpaId!, lines: linesWithOrder })).unwrap();
      message.success('Lines added successfully');
      setAddLineModalVisible(false);
      addLineForm.resetFields();
    } catch (error) {
      console.error('Error adding lines:', error);
      message.error('Failed to add lines');
    }
  };

  const handleAddSankalpa = async (values: any) => {
    try {
      console.log('handleAddSankalpa called with values:', values);
      const { tagIds, ...sankalpaData } = values;
      console.log('tagIds:', tagIds);
      console.log('sankalpaData:', sankalpaData);
      
      // Create the sankalpa first
      const result = await dispatch(createSankalpa(sankalpaData)).unwrap();
      const sankalpaId = result.id;
      console.log('Created sankalpa with ID:', sankalpaId);
      
      if (!sankalpaId) {
        throw new Error('Failed to create sankalpa');
      }
      
      // Associate tags (all are existing IDs now since modal creates them)
      if (tagIds && Array.isArray(tagIds)) {
        console.log('Associating tags:', tagIds);
        for (const tagId of tagIds) {
          if (tagId) {
            console.log('Associating tag', tagId, 'with sankalpa', sankalpaId);
            await sankalpaTagAPI.associateTagWithSankalpa(tagId, sankalpaId);
          }
        }
      }
      
      await dispatch(fetchSankalpas());
      await loadAllTags();
      message.success('Sankalpa created successfully');
      setAddSankalpaModalVisible(false);
    } catch (error) {
      console.error('Error creating sankalpa:', error);
      message.error('Failed to create sankalpa');
    }
  };

  const handleEditSankalpa = async (values: any) => {
    try {
      console.log('=== EDIT SANKALPA START ===');
      console.log('Raw form values:', JSON.stringify(values, null, 2));
      console.log('values.tagIds type:', typeof values.tagIds);
      console.log('values.tagIds isArray:', Array.isArray(values.tagIds));
      console.log('values.tagIds:', values.tagIds);
      
      await dispatch(updateSankalpa({ id: selectedSankalpaId!, data: { name: values.name } })).unwrap();
      
      // Update tags
      const currentSankalpa = sankalpas.find(s => s.id === selectedSankalpaId);
      const currentTagIds = currentSankalpa?.tags?.map(tag => tag.id) || [];
      
      // Process tagIds - handle both array and undefined
      let newTagIds: number[] = [];
      if (values.tagIds) {
        if (Array.isArray(values.tagIds)) {
          newTagIds = values.tagIds.filter((id: any) => id !== undefined && id !== null) as number[];
        }
      }
      
      console.log('Current tags:', currentTagIds);
      console.log('New tags (processed):', newTagIds);
      
      const tagsToRemove = currentTagIds.filter(id => !newTagIds.includes(id));
      const tagsToAdd = newTagIds.filter(id => !currentTagIds.includes(id));
      
      console.log('Tags to remove:', tagsToRemove);
      console.log('Tags to add:', tagsToAdd);
      
      for (const tagId of tagsToRemove) {
        console.log('Removing tag', tagId, 'from sankalpa', selectedSankalpaId);
        await sankalpaTagAPI.removeTagFromSankalpa(tagId, selectedSankalpaId!);
      }
      
      for (const tagId of tagsToAdd) {
        console.log('Adding tag', tagId, 'to sankalpa', selectedSankalpaId);
        try {
          const response = await sankalpaTagAPI.associateTagWithSankalpa(tagId, selectedSankalpaId!);
          console.log('Association response:', response);
        } catch (err) {
          console.error('Error associating tag:', err);
          throw err;
        }
      }
      
      console.log('=== EDIT SANKALPA END ===');
      
      console.log('Fetching updated sankalpas...');
      await dispatch(fetchSankalpas());
      console.log('Sankalpas fetched, current state:', sankalpas.find(s => s.id === selectedSankalpaId));
      message.success('Sankalpa updated successfully');
      setEditSankalpaModalVisible(false);
      editSankalpaForm.resetFields();
    } catch (error) {
      console.error('Error updating sankalpa:', error);
      message.error('Failed to update sankalpa');
    }
  };

  const handleEditLine = async (values: {
    content: string;
    order?: number;
    tags?: string[];
    metadata?: Array<{ key: string; value: string }>;
  }) => {
    try {
      const metadata = buildMetadataWithTags(values.metadata || [], values.tags || []);

      await dispatch(updateSankalpaLine({
        lineId: selectedLineId!,
        data: {
          content: values.content,
          order: values.order,
          metadata,
        },
      })).unwrap();
      message.success('Line updated successfully');
      setEditLineModalVisible(false);
      setEditLineContent('');
      editLineForm.resetFields();
    } catch (error) {
      console.error('Error updating line:', error);
      message.error('Failed to update line');
    }
  };

  const handleDeleteLine = async (lineId: number) => {
    try {
      await dispatch(deleteSankalpaLine(lineId)).unwrap();
      message.success('Line deleted successfully');
    } catch (error) {
      console.error('Error deleting line:', error);
      message.error('Failed to delete line');
    }
  };

  const handleDeleteSankalpa = async (id: number) => {
    try {
      await dispatch(deleteSankalpa(id)).unwrap();
      message.success('Sankalpa deleted successfully');
    } catch (error) {
      console.error('Error deleting sankalpa:', error);
      message.error('Failed to delete sankalpa');
    }
  };

  const handleEditSankalpaClick = (sankalpa: any) => {
    setSelectedSankalpaId(sankalpa.id);
    const tagIds = sankalpa.tags?.map((tag: any) => tag.id) || [];
    console.log('Setting edit form with tags:', tagIds);
    editSankalpaForm.setFieldsValue({ 
      name: sankalpa.name,
      tagIds: tagIds
    });
    setEditSankalpaModalVisible(true);
  };

  const handleEditLineClick = (sankalpa: any, line: SankalpaLine) => {
    setSelectedSankalpaId(sankalpa.id);
    setSelectedLineId(line.id!);
    setEditLineContent(line.content);
    editLineForm.setFieldsValue({
      content: line.content,
      order: line.order,
      tags: extractMetadataTagNames(line.metadata),
      metadata: metadataToFormEntries(line.metadata),
    });
    setEditLineModalVisible(true);
  };

  const parseSankalpaText = (text: string): Record<string, string> => {
    const regex = /\(अमुक\)\s+(\S+)/g;
    const extracted: Record<string, string> = {};
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      const key = match[1];
      if (key && !extracted[key]) {
        extracted[key] = '';
      }
    }
    
    return extracted;
  };

  const handleExtractClick = (sankalpa: any) => {
    // Get all lines content combined
    const allLinesContent = sankalpa.lines?.map((line: SankalpaLine) => line.content).join(' ') || '';
    setSelectedSankalpaId(sankalpa.id);
    setSelectedLineContent(allLinesContent);
    
    // Parse and extract metadata
    const extractedMetadata = parseSankalpaText(allLinesContent);
    
    // Convert to Form.List format
    const metadataList = Object.entries(extractedMetadata).map(([key, value]) => ({
      key,
      value
    }));
    
    extractForm.setFieldsValue({ metadata: metadataList });
    setExtractModalVisible(true);
  };

  const handleExtractSubmit = async (values: { metadata: { key: string; value: string }[] }) => {
    try {
      // Convert Form.List format back to object
      const metadataObj: Record<string, string> = {};
      values.metadata.forEach(item => {
        if (item.key) {
          metadataObj[item.key] = item.value || '';
        }
      });

      // Update all lines in the sankalpa with the extracted metadata
      if (selectedSankalpaId && sankalpas.find(s => s.id === selectedSankalpaId)?.lines) {
        const sankalpa = sankalpas.find(s => s.id === selectedSankalpaId);
        if (sankalpa?.lines) {
          // Update each line with metadata
          for (const line of sankalpa.lines) {
            if (line.id) {
              await dispatch(updateSankalpaLine({
                lineId: line.id,
                data: {
                  content: line.content,
                  order: line.order,
                  metadata: { ...line.metadata, ...metadataObj }
                }
              })).unwrap();
            }
          }
        }
      }
      
      message.success('Metadata extracted and saved successfully');
      setExtractModalVisible(false);
      extractForm.resetFields();
    } catch (error) {
      console.error('Error saving extracted metadata:', error);
      message.error('Failed to save extracted metadata');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>Sankalpa Management</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddSankalpaModalVisible(true)}
        >
          Add Sankalpa
        </Button>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        <Input
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          style={{ width: 300 }}
        />
        <Select
          mode="multiple"
          placeholder="Filter by tags"
          value={selectedTagIds}
          onChange={setSelectedTagIds}
          style={{ width: 300 }}
          options={allTags.map(tag => ({ label: tag.name, value: tag.id }))}
          allowClear
        />
      </div>

      <Card loading={loading}>
        <div>
          {sankalpas
            .filter(s => {
              const matchesName = !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase());
              const matchesTags = selectedTagIds.length === 0 || s.tags?.some(t => selectedTagIds.includes(t.id));
              return matchesName && matchesTags;
            })
            .map((sankalpa) => (
            <Card key={sankalpa.id} size="small" style={{ marginBottom: 8 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <strong>{sankalpa.name}</strong>
                {sankalpa.tags && sankalpa.tags.length > 0 && (
                  <Space size={4}>
                    {sankalpa.tags.map((tag: any) => (
                      <Tag key={tag.id} color="blue">{tag.name}</Tag>
                    ))}
                  </Space>
                )}
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEditSankalpaClick(sankalpa)}
                />
                <Popconfirm title="Delete sankalpa?" onConfirm={() => handleDeleteSankalpa(sankalpa.id!)}>
                  <Button size="small" icon={<DeleteOutlined />} danger />
                </Popconfirm>
              </div>
              {sankalpa.lines && sankalpa.lines.length > 0 && (
                sankalpa.lines.map((line, index) => (
                  <div key={line.id || index} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span>{line.content}</span>
                    {extractMetadataTagNames(line.metadata).map((tag) => (
                      <Tag key={tag} style={{ margin: 0 }}>{tag}</Tag>
                    ))}
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEditLineClick(sankalpa, line)}
                    />
                    <Popconfirm title="Delete line?" onConfirm={() => handleDeleteLine(line.id!)}>
                      <Button size="small" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                  </div>
                ))
              )}
              <div style={{ marginTop: '8px' }}>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedSankalpaId(sankalpa.id || null);
                    addLineForm.setFieldsValue({ lines: [{ content: '' }] });
                    setAddLineModalVisible(true);
                  }}
                >
                  Add Line
                </Button>
                <Button
                  size="small"
                  style={{"marginLeft": "10px"}}
                  icon={<PlusOutlined />}
                  onClick={() => handleExtractClick(sankalpa)}
                >
                  Extract
                </Button>
              </div>
            </div>
            </Card>
          ))}
        </div>
      </Card>

      <AddLineModal
        visible={addLineModalVisible}
        onCancel={() => setAddLineModalVisible(false)}
        onSubmit={handleAddLines}
        tagOptions={lineTagOptions}
      />

      <AddSankalpaModal
        visible={addSankalpaModalVisible}
        onCancel={() => setAddSankalpaModalVisible(false)}
        onSubmit={handleAddSankalpa}
      />

      {/* Edit Sankalpa Modal */}
      <Modal
        title="Edit Sankalpa"
        open={editSankalpaModalVisible}
        onCancel={() => {
          setEditSankalpaModalVisible(false);
          editSankalpaForm.resetFields();
        }}
        onOk={() => editSankalpaForm.submit()}
      >
        <Form form={editSankalpaForm} layout="vertical" onFinish={handleEditSankalpa}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please enter sankalpa name' }]}>
            <Input placeholder="Enter sankalpa name" />
          </Form.Item>
          <Form.Item label="Tags">
            <Form.List name="tagIds">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ display: 'flex', marginBottom: 8 }}>
                      <Form.Item
                        {...restField}
                        name={name}
                        style={{ marginBottom: 0, flex: 1 }}
                      >
                        <Select
                          showSearch
                          placeholder="Search or create tag"
                          filterOption={(input, option) =>
                            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                          }
                          options={allTags.map(tag => ({
                            label: tag.name,
                            value: tag.id
                          }))}
                        />
                      </Form.Item>
                      <Button
                        onClick={() => remove(name)}
                        style={{ marginLeft: 8 }}
                        danger
                        icon={<MinusCircleOutlined />}
                      />
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Tag
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Line Modal */}
      <Modal
        title="Edit Line"
        open={editLineModalVisible}
        width={640}
        onCancel={() => {
          setEditLineModalVisible(false);
          setSelectedLineId(null);
          setEditLineContent('');
          editLineForm.resetFields();
        }}
        onOk={() => editLineForm.submit()}
      >
        <Form form={editLineForm} layout="vertical" onFinish={handleEditLine}>
          <Form.Item label="Content" name="content" rules={[{ required: true, message: 'Please enter line content' }]}>
            <Input.TextArea placeholder="Enter line content" rows={4} />
          </Form.Item>
          <Form.Item label="Order" name="order">
            <InputNumber placeholder="Order" min={1} style={{ width: '100%' }} />
          </Form.Item>

          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Tags</div>
            <Form.Item name="tags" style={{ marginBottom: 0 }}>
              <BidhiMetadataTagsSelect tagOptions={lineTagOptions} />
            </Form.Item>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Metadata</div>
            <Form.List name="metadata">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'key']}
                        rules={[{ required: true, message: 'Key required' }]}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input placeholder="Key" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'value']}
                        rules={[{ required: true, message: 'Value required' }]}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input placeholder="Value" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        onClick={() => remove(name)}
                        icon={<DeleteOutlined />}
                      />
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Metadata
                  </Button>
                </>
              )}
            </Form.List>
          </div>
        </Form>

        <SankalpaLineBhedPanel
          sankalpaLineId={selectedLineId}
          visible={editLineModalVisible}
          lineContent={editLineContent}
        />
      </Modal>

      {/* Extract Metadata Modal */}
      <Modal
        title="Extract Metadata from Sankalpa"
        open={extractModalVisible}
        onCancel={() => {
          setExtractModalVisible(false);
          extractForm.resetFields();
        }}
        onOk={() => extractForm.submit()}
        width={800}
      >
        <Form form={extractForm} layout="vertical" onFinish={handleExtractSubmit}>
          <Form.Item label="Source Text" style={{ marginBottom: 16 }}>
            <Input.TextArea
              value={selectedLineContent}
              readOnly
              rows={6}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>
          <Form.Item label="Extracted Metadata">
            <Form.List name="metadata">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'key']}
                        style={{ flex: 1, marginBottom: 0 }}
                        rules={[{ required: true, message: 'Key is required' }]}
                      >
                        <Input placeholder="Key (e.g., दिग्भागे)" readOnly />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'value']}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input placeholder="Value" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        onClick={() => remove(name)}
                        style={{ marginTop: 4 }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  {fields.length === 0 && (
                    <div style={{ padding: 16, textAlign: 'center', color: '#888' }}>
                      No metadata found. Make sure the text contains "(अमुक)" followed by words.
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SankalpaPage;